/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo } from 'react';

import { SpaceRoleType } from '@adp-arch/idl/developer_api';

import { useSpaceStore } from '../space';

/**
 * Hook to check current user's permission in the current space.
 *
 * Space Role Types:
 * - Owner (1): Full access to all features
 * - Admin (2): Can manage members, edit/delete all resources
 * - Member (3): Can only view and copy resources, cannot edit/delete/create
 *
 * @returns Object containing permission flags
 */
export const useSpacePermission = () => {
  const currentSpace = useSpaceStore(state => state.space);

  const permissions = useMemo(() => {
    const roleType = currentSpace?.space_role_type;

    // Owner and Admin have full management permissions
    const isOwner = roleType === SpaceRoleType.Owner;
    const isAdmin = roleType === SpaceRoleType.Admin;
    const isMember = roleType === SpaceRoleType.Member;

    // Can manage space settings (only Owner and Admin)
    const canManageSpace = isOwner || isAdmin;

    // Can edit/create/delete resources (only Owner and Admin)
    const canEditResource = isOwner || isAdmin;

    // Can copy resources (all roles)
    const canCopyResource = true;

    // Can view resources (all roles)
    const canViewResource = true;

    return {
      roleType,
      isOwner,
      isAdmin,
      isMember,
      canManageSpace,
      canEditResource,
      canCopyResource,
      canViewResource,
    };
  }, [currentSpace?.space_role_type]);

  return permissions;
};
